import { CardList } from '@eventbrite/eds-card-list';
import { Horizontal, Vertical } from '@eventbrite/eds-layout';
import { GenericLazyString } from '@eventbrite/i18n';
import {
    ChoiceCard,
    ExpansionPanel,
    ExpansionPanelContent,
    ExpansionPanelTrigger,
    Typography,
} from '@eventbrite/marmalade';
import { $FixMe } from '@eventbrite/ts-utils';
import classNames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import { TicketClassCategory } from '../../../constants';
import {
    IMAGE_SIZE,
    REBRANDING_IMAGE_SIZE,
} from '../../../containers/ticketSelection/constants';
import { shouldShowPromoCodePills } from '../../../containers/ticketSelection/utils/shouldShowPromoCodePills';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { isDonationTicket } from '../../../utils/tickets';
import { TicketVariantForSelectionListProps } from '../constants';
import TicketVariantCard from '../TicketVariantCard';
import { getDonationChangeActionPayload } from '../utils';
import defaultStyles from './MultipleTicketVariantsCard.module.scss';
import rebrandStyles from './MultipleTicketVariantsCard.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export interface MultipleTicketVariantsCardProps {
    category: TicketClassCategory;
    currencyFormat: string;
    id: string;
    image?: {
        url: string;
        original: {
            url: string;
        };
    };
    isAppRebrandActive?: boolean;
    isFirstTicket?: boolean;
    maximumQuantityPerOrder?: number | null;
    name: string;
    onDonationChange?: (payload: {
        variantId?: string;
        id: string;
        amount: any;
    }) => void;
    onQuantityChange: (payload: {
        variantId?: string;
        id: string;
        quantity: number;
    }) => void;
    priceRange: string;
    showJoinWaitlist: boolean;
    variants: Array<TicketVariantForSelectionListProps>;
}

const _calculateMaxQuantityAvailable = (
    maximumQuantityPerOrder: number,
    totalTicketsSelected: number,
    selectedQuantity: number,
) => maximumQuantityPerOrder - totalTicketsSelected + selectedQuantity;

const getTicketOptionsRange = ({
    variant,
    maximumQuantityPerTicketGroup,
    totalTicketsSelected,
}: {
    variant: MultipleTicketVariantsCardProps['variants'][number];
    maximumQuantityPerTicketGroup: MultipleTicketVariantsCardProps['maximumQuantityPerOrder'];
    totalTicketsSelected: number;
}) => {
    if (variant.category !== TicketClassCategory.admission) {
        return variant.ticketOptionsRange;
    }
    if (variant.isBOGOTicket) {
        // return only odd numbers for BOGO tickets
        return variant.ticketOptionsRange.filter(
            (ticketOption) => ticketOption % 2 == 0,
        );
    }

    /**
     *  Rule 1
     *     If the variant level's variantMaxQuantity is not null
     *     then variants must respect that
     *  Rule 2
     *    If the ticket level's maximumQuantityPerTicketGroup is not null,
     *    then the sum of quantity for the variants for the ticket must
     *    also respect that in addition to variant level's variantMaxQuantity
     *  Rule 3
     *    If the ticket level's maximumQuantityPerTicketGroup is null, then we only care
     *    about the variants variantMaxQuantity
     */
    const validRangeOptionMax = maximumQuantityPerTicketGroup
        ? _calculateMaxQuantityAvailable(
              maximumQuantityPerTicketGroup,
              totalTicketsSelected,
              variant.selectedQuantity,
          )
        : // We should get the largest number from the ticket options range in order to get the max quantity
          Math.max(...variant.ticketOptionsRange);

    return variant.ticketOptionsRange.filter(
        (ticketOption) => ticketOption <= validRangeOptionMax,
    );
};

export const MultipleTicketVariantsCard: FunctionComponent<
    MultipleTicketVariantsCardProps
> = ({
    currencyFormat,
    id,
    image,
    isAppRebrandActive = false,
    isFirstTicket = false,
    maximumQuantityPerOrder,
    name,
    onDonationChange,
    onQuantityChange,
    priceRange,
    variants,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const [isExpanded, setIsExpanded] = useState(true);

    const _handleQuantityChange = (
        variantId: string | null,
        quantity: number,
    ) => {
        const payload = {
            id,
            quantity,
            ...(variantId ? { variantId } : {}),
        };

        onQuantityChange(payload);
    };

    const _handleDonationChange = (variantId: string, amount: number) => {
        onDonationChange?.(
            getDonationChangeActionPayload(
                id,
                `${amount}`,
                currencyFormat,
                variantId,
            ),
        );
    };

    const totalTicketsSelected = variants.reduce(
        (acc, singleVariant) => acc + singleVariant.selectedQuantity,
        0,
    );

    const getImageFromVariants = (variants: Array<$FixMe>) => {
        const variantWithImage = variants.find((variant) => variant.image);
        return variantWithImage ? variantWithImage.image : null;
    };

    const mapImage = (
        name: string | GenericLazyString,
        image?: {
            url: string;
            original: {
                url: string;
            };
        },
    ) =>
        image
            ? {
                  ticketName: name.toString(),
                  fullSizeUrl: image?.original.url,
                  thumbnailUrl: image?.url,
              }
            : undefined;

    const items = variants.map((variant, index) => {
        const onQuantityChange = (quantity: number) => {
            _handleQuantityChange(variant.id, quantity);
        };

        const onDonationChange = ({ amount }: { amount: number }) => {
            _handleDonationChange(variant.id, amount);
        };

        const ticketOptionsRange = getTicketOptionsRange({
            variant,
            maximumQuantityPerTicketGroup: maximumQuantityPerOrder,
            totalTicketsSelected,
        });

        const isDonation = isDonationTicket(variant);

        const imageThumbnail = variant.image
            ? variant.image
            : image
            ? image
            : getImageFromVariants(variants);

        const mappedImage = mapImage(variant.name, imageThumbnail);

        const { amountOff, discountType, onSaleStatus } = variant;
        const { showDiscountedPill, showUnlockedPill } =
            shouldShowPromoCodePills({ amountOff, discountType, onSaleStatus });

        return {
            id: variant.id,
            content: (
                <TicketVariantCard
                    category={variant.category}
                    cost={variant.cost}
                    currencyFormat={currencyFormat}
                    currencySymbol={variant.currencySymbol ?? ''}
                    description={variant.description}
                    donationAmount={variant.donationAmount}
                    feesAndTaxes={variant.feeAndTax ?? []}
                    formattedSalesEndDate={variant.salesEnd ?? ''}
                    formattedSalesStartDate={variant.salesStart ?? ''}
                    hideSaleDates={variant.hideSaleDates}
                    hideSelect={variant.hideSelect}
                    image={mappedImage}
                    includeFee={variant.includeFee}
                    includeTax={variant.includeTax}
                    isAppRebrandActive={isAppRebrandActive}
                    isBOGOTicket={variant.isBOGOTicket ?? false}
                    isCurrencySuffix={false}
                    isDonation={isDonation}
                    isLastItem={index === variants.length - 1}
                    name={variant.name}
                    onDonationChange={onDonationChange}
                    onQuantityChange={onQuantityChange}
                    price={variant.displayPrice ?? null}
                    priceWithoutDiscounts={variant.primaryPrice ?? ''}
                    remainingQuantity={variant.quantityRemaining}
                    selectableQuantities={ticketOptionsRange}
                    selectedQuantity={variant.selectedQuantity}
                    showDiscountedPill={showDiscountedPill}
                    showJoinWaitlist={variant.showJoinWaitlist}
                    showUnlockedPill={showUnlockedPill}
                    ticketId={variant.id}
                    ticketStatus={variant.statusKey}
                    urgencySignals={null}
                />
            ),
        };
    });

    const itemsList = <CardList items={items} customClassName={styles.list} />;

    const sectionImageThumbnail = image
        ? image
        : getImageFromVariants(variants);

    const mappedSectionImage = mapImage(name, sectionImageThumbnail);

    const imageSize = isAppRebrandActive ? REBRANDING_IMAGE_SIZE : IMAGE_SIZE;
    const sectionImageThumbnailComponent = mappedSectionImage ? (
        <div className={styles.thumbnail}>
            <img
                data-testid="multiple-ticket-variants-card-header-image"
                width={imageSize}
                height={imageSize}
                src={mappedSectionImage.thumbnailUrl}
                alt="card-header-image"
            />
        </div>
    ) : null;

    const expansionPanelTriggerClassNames = classNames(
        styles.expansionPanelTrigger,
        {
            [styles.expansionPanelTriggerWithImage]:
                !!sectionImageThumbnailComponent,
        },
    );

    const headerClassNames = classNames(styles.header, {
        [styles.headerWithImage]: !!sectionImageThumbnailComponent,
        ['eds-l-pad-bot-4']: !isExpanded,
    });

    const nameAndPriceRange = (
        <Vertical className="eds-l-pad-hor-4">
            <Typography
                variant="body-lg"
                color="neutral-800"
                className={styles.name}
            >
                {name}
            </Typography>
            <Typography
                variant="body-md"
                color="neutral-600"
                className={styles.priceRange}
            >
                {priceRange}
            </Typography>
        </Vertical>
    );

    const header = isAppRebrandActive ? (
        <>
            {sectionImageThumbnailComponent}
            {nameAndPriceRange}
        </>
    ) : (
        <>
            {nameAndPriceRange}
            {sectionImageThumbnailComponent}
        </>
    );

    const cardContent = (
        <ExpansionPanel
            className={styles.expansionPanel}
            defaultOpen
            onOpenChange={(open) => setIsExpanded(open)}
            variant="ghost"
        >
            <ExpansionPanelTrigger className={expansionPanelTriggerClassNames}>
                <Horizontal className={headerClassNames}>{header}</Horizontal>
            </ExpansionPanelTrigger>
            <ExpansionPanelContent>{itemsList}</ExpansionPanelContent>
        </ExpansionPanel>
    );

    return (
        <ChoiceCard
            className={classNames(styles.card, {
                'eds-l-mar-top-4': !(isFirstTicket && isAppRebrandActive),
            })}
            data-testid="multiple-ticket-variants-card"
        >
            {cardContent}
        </ChoiceCard>
    );
};
